import React, { useRef, useState, useCallback } from 'react';

interface LineNumberedTextareaProps {
  value: string;
  onChange: (value: string) => void;
  selectedLine?: number;
  onLineSelect?: (lineNumber: number) => void;
  className?: string;
  placeholder?: string;
}

const LineNumberedTextareaComponent: React.FC<LineNumberedTextareaProps> = ({
  value,
  onChange,
  selectedLine,
  onLineSelect,
  className = '',
  placeholder = '',
}) => {
  const [editingLine, setEditingLine] = useState<number | null>(null);
  const [editingText, setEditingText] = useState<string>('');
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  const lines = value.split('\n');

  const handleLineClick = (lineIndex: number) => {
    if (onLineSelect) {
      onLineSelect(lineIndex + 1); // Line numbers are 1-indexed
    }
    setEditingLine(lineIndex);
    setEditingText(lines[lineIndex] || '');
    // Focus the textarea after state updates
    setTimeout(() => {
      editTextareaRef.current?.focus();
    }, 0);
  };

  const handleLineNumberClick = (lineIndex: number) => {
    if (onLineSelect) {
      onLineSelect(lineIndex + 1);
    }
  };

  const saveEdit = useCallback(() => {
    if (editingLine !== null) {
      const newLines = [...lines];
      newLines[editingLine] = editingText;
      onChange(newLines.join('\n'));
      setEditingLine(null);
      setEditingText('');
    }
  }, [editingLine, editingText, lines, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
      // Move to next line
      if (editingLine !== null && editingLine < lines.length - 1) {
        handleLineClick(editingLine + 1);
      }
    } else if (e.key === 'Escape') {
      setEditingLine(null);
      setEditingText('');
    }
  };

  const handleBlur = () => {
    saveEdit();
  };

  return (
    <div className="border border-gray-300 rounded-md overflow-y-auto font-mono text-sm" style={{ maxHeight: '100%' }}>
      {lines.map((line, index) => (
        <div
          key={index}
          className={`flex items-stretch ${selectedLine === index + 1 ? 'bg-blue-50' : ''}`}
        >
          {/* Line number */}
          <div
            className={`
              w-[40px] bg-gray-100 border-r border-gray-300 flex-shrink-0
              text-gray-500 cursor-pointer hover:bg-gray-200 text-right pr-2
              ${selectedLine === index + 1 ? 'bg-blue-100 !text-blue-700' : ''}
            `}
            style={{
              userSelect: 'none',
              lineHeight: '1.5',
              padding: '0.5rem 0.5rem 0.5rem 0',
              minHeight: '1.5em',
            }}
            onClick={() => handleLineNumberClick(index)}
          >
            {index + 1}
          </div>

          {/* Line content */}
          {editingLine === index ? (
            <textarea
              ref={editTextareaRef}
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              className="flex-1 leading-[1.5] py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
              style={{ minHeight: '200px' }}
              rows={8}
            />
          ) : (
            <div
              className="flex-1 leading-[1.5] py-2 px-3 cursor-text whitespace-pre-wrap break-words"
              onClick={() => handleLineClick(index)}
              style={{ minHeight: '1.5em' }}
            >
              {line || (index === 0 && lines.length === 1 && placeholder ? (
                <span className="text-gray-400">{placeholder}</span>
              ) : (
                '\u00A0'
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders when props haven't changed
export const LineNumberedTextarea = React.memo(LineNumberedTextareaComponent, (prevProps, nextProps) => {
  // Only re-render if these props have actually changed
  return (
    prevProps.value === nextProps.value &&
    prevProps.selectedLine === nextProps.selectedLine &&
    prevProps.className === nextProps.className &&
    prevProps.placeholder === nextProps.placeholder
    // Don't compare onChange and onLineSelect as they're functions and may have different references
  );
});
